import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { mapProjects, Project, ProjectBasicContact } from "../Types/Project/ProjectInterface";
import { mapProjectContacts, ProjectContact } from "../Types/Project/ProjectContact";
import { mapRoles, Role } from "../Types/UserRoleInterface";

interface ProjectSliceType {
    loading: boolean;
    error: string | null;
    allProjects: Project[];
    currentProjects: Project[];
    ongoingProjects: Project[];
    completedProject: Project[];
    loadingContact: boolean;
    errorContact: string | null;
    projectAllContact: ProjectContact[];
    loadingUserRole: boolean;
    errorUserRole: string | null;
    userRoles: Role[];
    selectedUserRole: Role | null;
    selectedUserType: string | null;
    selectedProjectBasicContact: ProjectBasicContact | null;
    projectContacts: ProjectContact[];
    projectContactsFiltered: ProjectContact[];
    selectedProjectDetails: Project | null;
    contactSearchKey: string;
    countAll: number;
    countEmployee: number;
    countProfessional: number;
    countClient: number;
}

const initialState: ProjectSliceType = {
    loading: false,
    error: null,
    allProjects: [],
    currentProjects: [],
    ongoingProjects: [],
    completedProject: [],
    loadingContact: false,
    errorContact: null,
    projectAllContact: [],
    loadingUserRole: false,
    errorUserRole: null,
    userRoles: [],
    selectedUserRole: null,
    selectedUserType: null,
    selectedProjectBasicContact: null,
    projectContacts: [],
    projectContactsFiltered: [],
    selectedProjectDetails: null,
    contactSearchKey: '',
    countAll: 0,
    countEmployee: 0,
    countProfessional: 0,
    countClient: 0,
}

const projectSlice = createSlice({
    name: 'projectSlice',
    initialState,
    reducers: {
        fetchProjectStart: (state) => {
            state.loading = true;
            // state.allProjects = [];
            // state.currentProjects = [];
            // state.ongoingProjects = [];
            // state.completedProject = [];
            state.error = null;
        },
        fetchProjectSuccess: (state, action: PayloadAction<any[]>) => {
            state.loading = false;

            state.allProjects = mapProjects(action.payload);
            state.currentProjects = mapProjects(action.payload.filter(contact => contact.status === 'Current'));
            state.ongoingProjects = mapProjects(action.payload.filter(contact => contact.status === 'Ongoing'));
            state.completedProject = mapProjects(action.payload.filter(contact => contact.status === 'Completed'));
        },
        fetchProjectFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        fetchContactStart: (state) => {
            state.loadingContact = true;
            state.projectAllContact = [];
            state.errorContact = null;
        },
        fetchContactSuccess: (state, action: PayloadAction<any[]>) => {
            state.loadingContact = false;
            state.projectAllContact = mapProjectContacts(action.payload);
        },
        fetchContactFailure: (state, action: PayloadAction<string>) => {
            state.loadingContact = false;
            state.errorContact = action.payload;
        },
        fetchUserRoleStart: (state) => {
            state.loadingUserRole = true;
            state.userRoles = [];
            state.errorUserRole = null;
        },
        fetchUserRoleSuccess: (state, action: PayloadAction<any[]>) => {
            state.loadingUserRole = false;
            state.userRoles = mapRoles(action.payload);

        },
        fetchUserRoleFailure: (state, action: PayloadAction<string>) => {
            state.loadingUserRole = false;
            state.errorUserRole = action.payload;
        },
        setSelectedUserRole: (state, action: PayloadAction<Role | null>) => {
            state.selectedUserRole = action.payload;

            if (state.selectedUserRole && state.selectedUserType) {
                const projectContacts = state.projectContacts;
                state.projectContactsFiltered = projectContacts.filter((contact) => (contact.role_name === state.selectedUserRole?.role_name) && (contact.contactType === state.selectedUserType))
            } else if (state.selectedUserRole) {
                const projectContacts = state.projectContacts;
                state.projectContactsFiltered = projectContacts.filter((contact) => (contact.role_name === state.selectedUserRole?.role_name))
            } else if (state.selectedUserType) {
                const projectContacts = state.projectContacts;
                state.projectContactsFiltered = projectContacts.filter((contact) => (contact.contactType === state.selectedUserType))
            } else {
                state.projectContactsFiltered = state.projectContacts;
            }
        },
        setSelectedUserType: (state, action: PayloadAction<string | null>) => {
            state.selectedUserType = action.payload;
            applyFilters(state);
        },
        setContactSearchKey: (state, action: PayloadAction<string>) => {
            state.contactSearchKey = action.payload;
            applyFilters(state);
        },
        setSelectedProjectBasicContact: (state, action: PayloadAction<ProjectBasicContact>) => {
            state.selectedProjectBasicContact = action.payload;
        },
        selectAddProjectContact: (state, action: PayloadAction<Project>) => {
            state.projectContacts = [];
            state.projectContactsFiltered = [];

            const basicContacts: ProjectBasicContact[] = action.payload.contact;
            const basicContactSet = new Set(basicContacts.map(contact => contact.id));

            state.projectContacts = state.projectAllContact.map(contact => ({
                ...contact,
                icChecked: basicContactSet.has(contact.id)
            }));

            state.projectContactsFiltered = state.projectContacts;
            state.countAll = state.projectAllContact.length;
            state.countEmployee = state.projectAllContact.filter((contact) => contact.contactType === 'Employee').length;
            state.countProfessional = state.projectAllContact.filter((contact) => contact.contactType === 'Professional').length;
            state.countClient = state.projectAllContact.filter((contact) => contact.contactType === 'Client').length;
        }, setCheckedProjectContactForAssign: (state, action: PayloadAction<ProjectContact>) => {
            const contactToUpdate = action.payload;
            const existingContact = state.projectContacts.find(contact => contact.id === contactToUpdate.id);
            const existingContactFiltered = state.projectContactsFiltered.find(contact => contact.id === contactToUpdate.id);

            if (existingContact) {
                existingContact.icChecked = !existingContact.icChecked;
            }

            if (existingContactFiltered) {
                existingContactFiltered.icChecked = !existingContactFiltered.icChecked;
            }
        }, setSelectedProjectDetails: (state, action: PayloadAction<Project>) => {
            state.loading = true;
            state.selectedProjectDetails = action.payload;
            state.loading = false;
        },

        fetchProjectSuccessAndSetSelectedProjectUpdate: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.allProjects = mapProjects(action.payload);
            state.currentProjects = mapProjects(action.payload.filter(contact => contact.status === 'Current'));
            state.ongoingProjects = mapProjects(action.payload.filter(contact => contact.status === 'Ongoing'));
            state.completedProject = mapProjects(action.payload.filter(contact => contact.status === 'Completed'));

            const previousSelectedId = state.selectedProjectDetails?.project_id;
            if (previousSelectedId) {
                state.selectedProjectDetails = state.allProjects.find(project => project.project_id === previousSelectedId) || null;
            } else {
                state.selectedProjectDetails = null; // Or set to the first professional in the list, etc.
            }
        },
    }
})

function applyFilters(state: ProjectSliceType) {
    let filtered = state.projectContacts;

    // Filter by user type if selected
    if (state.selectedUserType) {
        filtered = filtered.filter(
            (contact) => contact.contactType === state.selectedUserType
        );
    }

    // Filter by search key (firstName OR lastName)
    if (state.contactSearchKey) {
        const search = state.contactSearchKey.toLowerCase();
        filtered = filtered.filter(
            (contact) => {
                const firstName = contact.first_name ?? "";
                const lastName = contact.last_name ?? "";

                return (firstName.toLowerCase().includes(search) || lastName.toLowerCase().includes(search));
            }
        );
    }

    state.projectContactsFiltered = filtered;
}

export const { fetchProjectStart, fetchProjectFailure, fetchProjectSuccess, fetchContactStart, fetchContactSuccess, fetchContactFailure, fetchUserRoleStart, fetchUserRoleSuccess,
    fetchUserRoleFailure, setSelectedUserRole, setSelectedUserType, setContactSearchKey, setSelectedProjectBasicContact, selectAddProjectContact, setCheckedProjectContactForAssign,
    setSelectedProjectDetails, fetchProjectSuccessAndSetSelectedProjectUpdate } = projectSlice.actions;
export default projectSlice.reducer;
